import { supabase } from '../../config/supabase';
import { computeEstimationTotals, computeEffectivePricing, isDepartmentQuotable } from './finalPricingModes';

const LIST_PAGE_SIZE = 50;
// Safety cap on how many underlying DB pages a single loadMore scan will
// walk through while hunting for pricingStatus matches (see below). Keeps a
// single call bounded; scrolling further simply resumes the scan.
const MAX_SCAN_PAGES = 10;

export const FINAL_PRICING_DEPARTMENTS = [
    {
        key: 'roof',
        label: 'Roof',
    },
    {
        key: 'wall',
        label: 'Wall',
    },
    {
        key: 'floor',
        label: 'Floor',
    },
];

const SUPPORTED_DEPARTMENTS = FINAL_PRICING_DEPARTMENTS.map(
    department => department.key
);

const PRICING_SELECT = `
    id,
    company_id,
    job_id,
    department,
    final_price,
    notes,
    created_by,
    created_by_name,
    created_at,
    updated_by,
    updated_by_name,
    updated_at
`;

// Full select used for the single-job detail view (FinalPricingDetails.js).
const JOB_SELECT = `
    id,
    company_id,
    unique_id,
    customer_name,
    customer_mail,
    customer_number,
    customer_address,
    job_type,
    main_status,
    urgent_design,
    urgent_quote,
    is_cancelled,
    deleted,
    selected_departments,
    created_at,
    updated_at,
    job_final_pricings(${PRICING_SELECT}),
    job_pricing_entries(*)
`;

// job_pricing_entries is always selected in full now — every company can
// have fallback-priced departments via computeEffectivePricing, not just
// companies in a particular mode, so there's no longer a "lean" variant.
const LIST_JOB_SELECT = `
    id,
    company_id,
    unique_id,
    customer_name,
    customer_address,
    job_type,
    is_cancelled,
    created_at,
    job_final_pricings(${PRICING_SELECT}),
    job_pricing_entries(*)
`;

const asNumber = value => {
    if (
        value === null ||
        value === undefined ||
        value === ''
    ) {
        return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed)
        ? parsed
        : null;
};

const assertDepartment = department => {
    if (!SUPPORTED_DEPARTMENTS.includes(department)) {
        throw new Error(
            `Unsupported final pricing department: ${department}`
        );
    }
};

const normalizeFinalPricings = value => {
    const records = Array.isArray(value)
        ? value
        : value
            ? [value]
            : [];

    return records.reduce(
        (result, pricing) => {
            if (
                pricing?.department &&
                SUPPORTED_DEPARTMENTS.includes(pricing.department)
            ) {
                result[pricing.department] = pricing;
            }

            return result;
        },
        {
            roof: null,
            wall: null,
            floor: null,
        }
    );
};

const normalizeJob = job => ({
    ...job,

    // `address` is kept as an alias of `customer_address` (the real column)
    // so existing address rendering keeps working regardless of shape.
    address: job.customer_address ?? job.address ?? null,

    final_pricings: normalizeFinalPricings(
        job.job_final_pricings
    ),

    pricing_entries: job.job_pricing_entries || [],
});

export const hasAllFinalPricings = job =>
    SUPPORTED_DEPARTMENTS.every(
        key => !!job.final_pricings?.[key]
    );

// The "all departments priced" check backing the pricingStatus quick-filter
// buttons ("Missing department pricing" / "All pricing entered") in FinalPricings.js.
// Combines manual final prices with estimation-based fallback via
// computeEffectivePricing, then gates each department on isDepartmentQuotable
// (which respects the requireFinalPricing flag).
const jobHasAllDepartmentsPriced = (job, requireFinalPricing) => {
    const estimationTotals = computeEstimationTotals(job.pricing_entries);
    const effective = computeEffectivePricing({ finalPricings: job.final_pricings, estimationTotals });

    return SUPPORTED_DEPARTMENTS.every(
        key => isDepartmentQuotable(effective.departments[key], requireFinalPricing)
    );
};

const matchesPricingStatus = (job, pricingStatus, requireFinalPricing) => {
    if (pricingStatus === 'entered') return jobHasAllDepartmentsPriced(job, requireFinalPricing);
    if (pricingStatus === 'missing') return !jobHasAllDepartmentsPriced(job, requireFinalPricing);
    return true;
};

// PostgREST's `.or()` filter syntax treats `,` and `()` as structural
// characters — strip them from free-text search input so a query can't be
// broken (or reinterpreted) by user-typed punctuation.
const sanitizeSearchTerm = value =>
    String(value || '').trim().replace(/[,()%]/g, ' ').trim();

const runFinalPricingQuery = async (companyId, {
    from,
    to,
    search,
    jobType,
    cancelledStatus,
    sortBy,
}) => {
    let query = supabase
        .from('jobs')
        .select(LIST_JOB_SELECT, { count: 'exact' })
        .eq('company_id', companyId)
        .eq('deleted', false);

    const term = sanitizeSearchTerm(search);
    if (term) {
        // job_type is deliberately excluded here — it's a Postgres enum
        // column with no ILIKE/~~* operator of its own (enums only support
        // equality/ordering), and PostgREST's `or=(...)` logic-tree grammar
        // doesn't accept a `column::type` cast to work around that (only a
        // top-level filter param does). Exact-match job type filtering is
        // already available separately via the jobType dropdown filter
        // below.
        query = query.or(
            `unique_id.ilike.%${term}%,customer_name.ilike.%${term}%,customer_address.ilike.%${term}%`
        );
    }

    if (jobType && jobType !== 'all') {
        query = query.eq('job_type', jobType);
    }

    if (cancelledStatus === 'active') {
        query = query.eq('is_cancelled', false);
    } else if (cancelledStatus === 'cancelled') {
        query = query.eq('is_cancelled', true);
    }

    switch (sortBy) {
        case 'oldest':
            query = query.order('created_at', { ascending: true });
            break;
        case 'alphabetical':
            query = query.order('unique_id', { ascending: true });
            break;
        case 'reverse_alphabetical':
            query = query.order('unique_id', { ascending: false });
            break;
        default:
            // 'newest' and the price-based sorts (which have no backing DB
            // column — they're a sum across up to 3 related rows) fall back
            // to created_at desc; price sorts are re-applied client-side on
            // the currently loaded rows.
            query = query.order('created_at', { ascending: false });
    }

    query = query.range(from, to);

    const { data, error, count } = await query;
    if (error) throw error;

    return {
        rows: (data || []).map(normalizeJob),
        total: count || 0,
    };
};

/**
 * Fetch one "page" of final-pricing jobs, filtered entirely at the database
 * level (search / job type / cancelled state / sort). `pricingStatus`
 * ("entered" / "missing") isn't a plain column — it depends on how many of
 * the 3 department rows exist for a job (or, in estimation-based final
 * pricing modes, on summed job_pricing_entries amounts per department — see
 * `requireFinalPricing` / jobHasAllDepartmentsPriced above) — so when it's active this
 * scans forward through DB pages (bounded by MAX_SCAN_PAGES) collecting
 * matches, advancing the returned `nextOffset` by exactly the number of raw
 * rows consumed. That guarantees every job is evaluated exactly once in
 * order — nothing gets silently skipped the way a "fetch page → filter"
 * approach would when a page happens to contain few/no matches.
 */
export const getFinalPricingJobsPage = async (companyId, {
    offset = 0,
    limit = LIST_PAGE_SIZE,
    search = '',
    jobType = 'all',
    cancelledStatus = 'active',
    pricingStatus = 'all',
    sortBy = 'newest',
    requireFinalPricing = true,
} = {}) => {
    if (!companyId) {
        return { data: [], nextOffset: offset, hasMore: false, total: 0 };
    }

    const matches = [];
    let cursor = offset;
    let total = 0;
    let dbExhausted = false;

    for (let scanned = 0; scanned < MAX_SCAN_PAGES && matches.length < limit; scanned += 1) {
        const { rows, total: rowTotal } = await runFinalPricingQuery(companyId, {
            from: cursor,
            to: cursor + limit - 1,
            search,
            jobType,
            cancelledStatus,
            sortBy,
        });

        total = rowTotal;

        let consumed = 0;
        for (const job of rows) {
            consumed += 1;
            if (matchesPricingStatus(job, pricingStatus, requireFinalPricing)) {
                matches.push(job);
                if (matches.length >= limit) break;
            }
        }
        cursor += consumed;

        if (rows.length === 0 || cursor >= total) {
            dbExhausted = true;
            break;
        }
    }

    return {
        data: matches,
        nextOffset: cursor,
        hasMore: !dbExhausted,
        // Exact total is only meaningful when there's no post-filter.
        total: pricingStatus === 'all' ? total : null,
    };
};

export const getFinalPricingJob = async (
    companyId,
    jobId
) => {
    const { data, error } = await supabase
        .from('jobs')
        .select(JOB_SELECT)
        .eq('company_id', companyId)
        .eq('id', jobId)
        .eq('deleted', false)
        .single();

    if (error) throw error;

    return normalizeJob(data);
};

// Lean single-job fetch of job_final_pricings only — for callers that just
// need per-department final price existence/amount without the rest of
// getFinalPricingJob's heavy joins (full job columns, job_pricing_entries).
// Used by PricingReviewDropdown.js's own isolated status-strip fetch.
// Returns a map keyed by department; only departments with an actual row are
// present, so `department in result` (or Object.prototype.hasOwnProperty)
// is how callers should check existence — a legitimately-entered $0 final
// price must not be confused with "not entered" (see FinalPricingCalendar.js's
// isEntryPriced for the same existence-vs-amount distinction).
export const getFinalPricingsForJob = async (companyId, jobId) => {
    const { data, error } = await supabase
        .from('job_final_pricings')
        .select('department, final_price')
        .eq('company_id', companyId)
        .eq('job_id', jobId);

    if (error) throw error;

    const finalPricings = {};
    (data || []).forEach(row => {
        if (row?.department) {
            finalPricings[row.department] = row.final_price;
        }
    });

    return finalPricings;
};

export const saveFinalPricing = async ({
    companyId,
    jobId,
    department,
    finalPrice,
    notes,
    userProfile,
}) => {
    assertDepartment(department);

    const numericPrice = asNumber(finalPrice);

    if (
        numericPrice === null ||
        numericPrice < 0
    ) {
        throw new Error(
            'Final price must be a valid positive amount.'
        );
    }

    const userName =
        userProfile?.full_name ||
        userProfile?.username ||
        userProfile?.email ||
        'Unknown user';

    const payload = {
        company_id: companyId,
        job_id: jobId,
        department,
        final_price: numericPrice,
        notes: notes?.trim() || null,
        updated_by: userProfile?.id || null,
        updated_by_name: userName,
        updated_at: new Date().toISOString(),
    };

    const { data: existing, error: existingError } =
        await supabase
            .from('job_final_pricings')
            .select('id')
            .eq('company_id', companyId)
            .eq('job_id', jobId)
            .eq('department', department)
            .maybeSingle();

    if (existingError) {
        throw existingError;
    }

    if (existing?.id) {
        const { data, error } = await supabase
            .from('job_final_pricings')
            .update(payload)
            .eq('id', existing.id)
            .select(PRICING_SELECT)
            .single();

        if (error) throw error;

        return data;
    }

    const { data, error } = await supabase
        .from('job_final_pricings')
        .insert({
            ...payload,
            created_by: userProfile?.id || null,
            created_by_name: userName,
        })
        .select(PRICING_SELECT)
        .single();

    if (error) throw error;

    return data;
};

export const deleteFinalPricing = async ({
    companyId,
    jobId,
    department,
}) => {
    assertDepartment(department);

    const { error } = await supabase
        .from('job_final_pricings')
        .delete()
        .eq('company_id', companyId)
        .eq('job_id', jobId)
        .eq('department', department);

    if (error) throw error;
};

export const subscribeToFinalPricings = (
    companyId,
    callback
) => {
    if (!companyId) return null;

    return supabase
        .channel(`final-pricings-${companyId}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'job_final_pricings',
                filter: `company_id=eq.${companyId}`,
            },
            callback
        )
        .subscribe();
};

export const unsubscribeFromFinalPricings =
    async channel => {
        if (channel) {
            await supabase.removeChannel(channel);
        }
    };
